/**
 * Вид «Отзывов» как у чужих воронок — слово владельца 21.10.2026 (ветка
 * stylepack/testimonial-look). Зеркало серверной схемы
 * `backend/src/ai-funnels/block-schema/testimonial-look.ts`: перечень
 * раскладов и потолок отметки сверяет сторож
 * `testimonial-look.ends.spec.ts` в обе стороны.
 *
 * Здесь же зажим отрисовки: ручное сохранение и старые черновики могут нести
 * что угодно, поэтому карточка берёт только то, что прошло бы схему, а всё
 * прочее — как без поля (прежний вид).
 */
import { isAllowedUrl } from '@/utils/isAllowedUrl';
import { RATING_STAR_COLOR } from './ratingStarColor';

/** 'left' — фото слева во всю высоту; 'top' — сверху во всю ширину; 'avatar' — круг у имени. */
export const TESTIMONIAL_PHOTO_LAYOUTS = ['left', 'top', 'avatar'] as const;
export type TestimonialPhotoLayout = (typeof TESTIMONIAL_PHOTO_LAYOUTS)[number];

/** Потолок отметки — тот же, что у схемы сервера. */
export const TESTIMONIAL_BADGE_MAX = 24;

/** Размеры фото по раскладам (задание: ~35 % слева, 160 px сверху, круг 40 px). */
export const TESTIMONIAL_LEFT_PHOTO_WIDTH = '35%';
export const TESTIMONIAL_TOP_PHOTO_HEIGHT = 160;
export const TESTIMONIAL_AVATAR_SIZE = 40;

/** Поля блока, которые читает карточка. */
export interface TestimonialLookFields {
  testimonialPhotoLayout?: TestimonialPhotoLayout;
  testimonialCardBackground?: string;
  testimonialQuoteMarks?: boolean;
  testimonialItalic?: boolean;
  testimonialBadgeColor?: string;
}

export interface TestimonialLook {
  layout: TestimonialPhotoLayout;
  quoteMarks: boolean;
  italic: boolean;
  cardBackground: string | undefined;
  badgeColor: string;
}

/** Код цвета: #rgb, #rrggbb, #rrggbbaa — зеркало BLOCK_COLOR_PATTERN сервера. */
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const hex = (raw: unknown): string | undefined =>
  typeof raw === 'string' && HEX_COLOR.test(raw) ? raw : undefined;

export function resolveTestimonialLook(
  fields: TestimonialLookFields,
): TestimonialLook {
  const layout = (TESTIMONIAL_PHOTO_LAYOUTS as readonly string[]).includes(
    fields.testimonialPhotoLayout as string,
  )
    ? (fields.testimonialPhotoLayout as TestimonialPhotoLayout)
    : 'avatar';
  return {
    layout,
    quoteMarks: fields.testimonialQuoteMarks !== false,
    italic: fields.testimonialItalic !== false,
    cardBackground: hex(fields.testimonialCardBackground),
    badgeColor: hex(fields.testimonialBadgeColor) ?? RATING_STAR_COLOR,
  };
}

/** Адрес фото автора: только через общую дверь адресов, пустой — без фото. */
export function testimonialPhotoSrc(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const value = raw.trim();
  return value !== '' && isAllowedUrl(value) ? value : undefined;
}

/** Текст отметки: пустая — без отметки, длиннее потолка — срезается. */
export function testimonialBadgeText(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const value = raw.trim();
  return value === '' ? undefined : value.slice(0, TESTIMONIAL_BADGE_MAX);
}
